/**
 * @file src/views/VID/components/CameraView/styles.ts
 * @exports AnimatedCameraCircleWrapper, ButtonWrapper, CAPTURE_MODAL_Z_INDEX, CAPTURE_OVERLAY_Z_INDEX, CancelButtonWrapper, DynamicTextWrapper, Header, LoadingWrapper, LogoWrapper, LongText, Overlay, OverlayWrapper, PhotoActionButtons, PreviewImage, ROTATION_WARNING_Z_INDEX, ResponsiveCanvas, SelfieInfo, ShortText, SquareCanvas, SubHeader, TEXT_WRAPPING_POINT, TextWrapper, TopBarWrapper, Video, VideoWrapper, Wrapper
 * @summary Shared styled-components and layout constants for the vID camera capture views, plus the
 *   z-index ladder that keeps dialogs layered correctly over the fullscreen capture overlay.
 */

import { HeadingH1, colors } from "@cerebruminc/cerebellum";
import styled from "styled-components";

export const TEXT_WRAPPING_POINT = 870;

/**
 * Stacking floor for the fullscreen capture view. Mantine portals its modals to `<body>`, where the
 * default modal z-index (200) loses to this, so every dialog shown over the capture view needs an
 * explicit z-index from the two constants below.
 */
export const CAPTURE_OVERLAY_Z_INDEX = 1000;
/** Dialogs layered over the capture view (e.g. the scan tips). */
export const CAPTURE_MODAL_Z_INDEX = 1100;
/** The rotation warning outranks every other capture-view dialog — the device must be turned either way. */
export const ROTATION_WARNING_Z_INDEX = 1200;

export const Wrapper = styled.div<{ $highContrast: boolean; $visible: boolean }>`
  background: ${({ $highContrast }) =>
		$highContrast ? colors.WHITE : "linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 50%, #cbd3f5 100%)"};
  background-attachment: fixed;
  bottom: -500px;
  display: ${({ $visible }) => ($visible ? "flex" : "none")};
  flex-direction: column;
  left: 0;
	overflow-y: auto;
	padding-bottom: 500px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: ${CAPTURE_OVERLAY_Z_INDEX};
  @media (max-height: 500px) {
		overflow-y: scroll;
  }
`;

export const Header = styled.div<{ $row?: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: 30px 40px 0px 40px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 30px 20px 0px 20px;
  }

  @media (max-width: 480px) {
    flex-direction: ${({ $row }) => ($row ? "row" : "column")};
    align-items: center;
  }
`;

export const SubHeader = styled.div<{ $marginTop?: string }>`
  display: flex;
  margin-top: 40px;
  padding: 0 30px;
  width: 100%;

  @media (max-width: 480px) {
    text-align: center;
  }
`;

export const CancelButtonWrapper = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
`;

export const LogoWrapper = styled.div`
  width: 94px;
  height: 40px;
`;

export const TextWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex: 1;
	text-align: center;
`;

export const DynamicTextWrapper = styled.div`
  position: relative;
`;

export const LongText = styled(HeadingH1)<{ $visible: boolean }>`
	color: ${colors.COOL_GREY_100};
	visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
	@media (max-width: 790px) {
    font-size: 24px;
  }
	@media (max-width: 640px) {
    font-size: 22px;
  }
	@media (max-width: ${TEXT_WRAPPING_POINT}px) {
    line-height: 30px;
  }
  @media (max-width: 320px) {
    font-size: 18px;
    line-height: 24px;
  }
`;

export const ShortText = styled(LongText)`
	position: absolute;
	/* HeadingH1 line-height is 40px */
	top: calc(50% - 20px);
	width: 100%;
	@media (max-width: ${TEXT_WRAPPING_POINT}px) {
    top: calc(50% - 15px);
  }
`;

export const VideoWrapper = styled.div<{ $size: number; $visible?: boolean }>`
  display: ${({ $visible }) => ($visible ? "block" : "none")};
  border-radius: 18px;
  height: ${({ $size }) => $size}px;
  margin: 30px auto 25px;
	min-height: 190px;
  position: relative;
	width: ${({ $size }) => $size}px;

	@media (max-width: 710px) {
		margin-top: 5px;
	}
`;
export const Video = styled.video<{ $visible: boolean; $ready: boolean }>`
  border-radius: 18px;
  display: ${({ $visible }) => ($visible ? "block" : "none")};
  height: 100%;
  object-fit: cover;
  position: relative;
	visibility: ${({ $ready }) => ($ready ? "visible" : "hidden")};
  width: 100%;
`;

export const PreviewImage = styled.img<{ $visible?: boolean }>`
  border-radius: 18px;
  display: ${({ $visible }) => ($visible ? "block" : "none")};
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

export const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
	min-height: 94px;
  padding-bottom: 63px;
  width: 100%;
	@media (max-width: 768px) {
    padding-bottom: 37px;
		min-height: 68px;
  }
`;

export const ResponsiveCanvas = styled.canvas`
  display: none;
  height: 100%;
  width: 100%;
`;

export const SquareCanvas = styled.canvas`
  display: none;
  height: 500px;
  width: 500px;
`;

export const TopBarWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  width: 100%;
`;

export const LoadingWrapper = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  position: absolute;
  width: 100%;
	z-index: 10;
`;

export const AnimatedCameraCircleWrapper = styled.div<{ $animate: boolean }>`
  align-items: center;
  display: flex;
  height: 80%;
  justify-content: center;
  margin: 0 10%;
  opacity: ${({ $animate }) => ($animate ? 1 : 0.25)};
  transition: all 0.15s ease-in-out;
  width: 100%;

  > svg {
    height: 100%;
    width: 100%;
  }
`;

export const OverlayWrapper = styled.div`
  align-items: center;
  height: 100%;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

export const Overlay = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  position: relative;

  > svg {
    height: 100%;
    width: 100%;
  }
`;

export const PhotoActionButtons = styled.div<{ $visible: boolean }>`
  display: flex;
  gap: 10px;
  justify-content: center;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
  padding: 10px 0;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;

export const SelfieInfo = styled.div<{ $visible?: boolean }>`
  margin-bottom: 10px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
	padding: 0 30px;
  text-align: center;
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
`;
